import React from 'react';

import { get, isEmpty } from 'lodash';
import MemberColumn from '../columnDefs/Member';
import Generic from '../columnDefs/Generic';
import { Row } from '@tanstack/react-table';
import { PostInsights } from '@frontend/gql/social/graphql';
import {
  SocialMediaThumbnail,
  getContentTypeLabel,
  isContentTypeEnum,
} from '@frontend/applications/Social/components/thumbnail/SocialMediaThumbnail';
import { SocialColumn } from '../../columnSelectorDialog/hooks/useColumnDialog';
import Percentage from '../columnDefs/Percentage';
import Currency from '../columnDefs/Currency';
import { getSocialAccountLink } from '@frontend/utils';
import { isNetworkIdentifier } from '@components';
import { formatDisplayDate } from '../../date-range-form/utils';
import { ENTITY_TYPE_TO_POST_TYPE, POST_TYPE_LABELS } from '@frontend/applications/Social/types';

export const POST_TABLE_COLUMN_CONFIG_VERSION = 6; // must update this value if you change anything below

export interface PostTableColumn<T = any> extends SocialColumn {
  header: string;
  id: string;
  enableSorting?: boolean;
  enableGlobalFiltering?: boolean;
  hidden: boolean;
  size?: number;
  minSize?: number;
  maxSize?: number;
  accessorFn?: (row: T) => unknown;
  accessorKey?: string; // accessorKey or accessorFn should be set
  cell: (props: { row: Row<T> }) => React.ReactNode;
  csvValue: (props: { data: T }) => string | null | undefined;
}

export const defaultVisiblePostColumnIds = [
  'content',
  'member',
  'socialHandle',
  'postDate',
  'project',
  'postPermalink',
  'entityType',
  'total.impressions',
  'total.engagements',
  'total.engagementRate',
  'total.tmv',
];

export const getColumns = (ids?: string[]): PostTableColumn<PostInsights>[] => {
  return !isEmpty(ids) ? AllColumns.filter((col) => ids.includes(col.id)) : AllColumns.filter((col) => defaultVisiblePostColumnIds.includes(col.id));
}

export const getColumnsWithHiddenColumns = (
  ids: string[] = defaultVisiblePostColumnIds
): PostTableColumn<PostInsights>[] => {
  const applyHidden = (col: PostTableColumn<PostInsights>): PostTableColumn<PostInsights> => ({
    ...col,
    hidden: !ids.includes(col.id),
    subColumns: col.subColumns?.map(applyHidden),
  });
  return AllColumns.map(applyHidden);
}

export const AllColumns: PostTableColumn<PostInsights>[] = [
  {
    header: 'Content',
    id: 'content',
    accessorKey: 'content',
    enableSorting: true,
    hidden: false,
    isFixed: true,
    maxSize: 90,
    csvValue: ({ data }) => {
      return get(data, 'postPermalink');
    },
    cell: ({ row }) => {
      const postUrl = get(row.original, 'postPermalink') ?? '';
      const postImageUrl = get(row.original, 'postImageUrl') ?? '';
      const memberName = get(row.original, 'member.name') ?? '';
      const entityType = get(row.original, 'entityType') ?? '';
      const entityNetwork = get(row.original, 'entityNetwork') ?? '';

      return (
        <a href={postUrl} target="_blank" rel="noopener noreferrer">
          <SocialMediaThumbnail
            postUrl={postUrl}
            postImageUrl={postImageUrl || undefined}
            memberName={memberName}
            entityType={entityType}
            entityNetwork={entityNetwork}
          />
        </a>
      );
    },
  },
  {
    header: 'Member',
    id: 'member',
    enableSorting: true,
    enableGlobalFiltering: true,
    hidden: false,
    isFixed: true,
    size: 200,
    minSize: 50,
    maxSize: 300,
    accessorFn: (row) => row.member?.name ?? '',
    csvValue: ({ data }) => {
      const member = get(data, 'member');
      return member?.name ?? '';
    },
    cell: ({ row }) => {
      const member = get(row.original, 'member');
      if (!member) {
        return <Generic value="Unknown" aria-label="Member: Unknown" />;
      }
      const profilePicture = member.profilePicture || undefined;
      return (
        <MemberColumn
          name={member?.name || ''}
          image={profilePicture}
          url={member?.id ? `/members/${member.id}` : '#'}
          aria-label={`Member: ${member?.name || 'Unknown'}`}
          data-dd-action-name="member-table-member-click"
        />
      );
    },
  },
  {
    header: 'Social Handle',
    id: 'socialHandle',
    enableSorting: true,
    enableGlobalFiltering: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      const handle = get(data, 'accountUsername');
      return handle ?? '';
    },
    accessorFn: (row) => {
      const handle = get(row, 'accountUsername');
      return handle ?? '';
    },
    cell: ({ row }) => {
      const network = get(row.original, 'entityNetwork');
      const handle = get(row.original, 'accountUsername');
      if (isNetworkIdentifier(network)) {
        const socialLink = getSocialAccountLink(network, handle);
        return (
          <a href={socialLink} target="_blank" rel="noopener noreferrer">
            <Generic value={handle} className="underline" aria-label={`Post Link: ${handle || 'Not available'}`} />
          </a>
        );
      }
      return <Generic value={handle} aria-label={`Post Link: ${handle || 'Not available'}`} />;
    },
  },
  {
    header: 'Post Date',
    id: 'postDate',
    accessorKey: 'postDate',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      const value = get(data, 'postDate');
      return value ? formatDisplayDate(String(value)) : '';
    },
    cell: ({ row }) => {
      const value = get(row.original, 'postDate') || '';
      const formattedDate = value ? formatDisplayDate(String(value)) : '';
      return <Generic value={formattedDate} aria-label={`Post Date: ${formattedDate || 'Not available'}`} />;
    },
  },
  {
    header: 'Project',
    id: 'project',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'project.name');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'project.name');
      return <Generic value={value} aria-label={`Project: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Group',
    id: 'group',
    enableSorting: true,
    hidden: true,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'group.name');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'group.name');
      return <Generic value={value} aria-label={`Group: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Post Link',
    id: 'postPermalink',
    accessorKey: 'postPermalink',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return String(get(data, 'postPermalink') || '');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'postPermalink') || '';
      return (
        <a href={value} target="_blank" rel="noopener noreferrer">
          <Generic value={value} className="underline" aria-label={`Post Link: ${value ?? 'Not available'}`} />
        </a>
      );
    },
  },
  {
    header: 'Network',
    id: 'entityNetwork',
    accessorKey: 'entityNetwork',
    enableSorting: true,
    hidden: true,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'entityNetwork');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'entityNetwork');
      return <Generic value={value} aria-label={`Network: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Post Type',
    id: 'entityType',
    accessorKey: 'entityType',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      const rawType = get(data, 'entityType');

      const postType = ENTITY_TYPE_TO_POST_TYPE[rawType];
      if (postType) {
        return POST_TYPE_LABELS[postType];
      }

      const enumType = isContentTypeEnum(rawType) ? rawType : null;
      return getContentTypeLabel(enumType);
    },
    cell: ({ row }) => {
      const rawType = get(row.original, 'entityType');

      const postType = ENTITY_TYPE_TO_POST_TYPE[rawType];
      if (postType) {
        return POST_TYPE_LABELS[postType];
      }

      const enumType = isContentTypeEnum(rawType) ? rawType : null;
      const value = getContentTypeLabel(enumType);
      return <Generic value={value} aria-label={`Post Type: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Reach',
    id: 'total.reach',
    accessorKey: 'total.reach',
    enableSorting: true,
    hidden: true,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.reach');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.reach');
      return <Generic align={'right'} value={value} aria-label={`Reach: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Impressions',
    id: 'total.impressions',
    accessorKey: 'total.impressions',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.impressions');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.impressions');
      return <Generic align={'right'} value={value} aria-label={`Impressions: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Engagement',
    id: 'total.engagements',
    accessorKey: 'total.engagements',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.engagements');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.engagements');
      return <Generic align={'right'} value={value} aria-label={`Engagement: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Engagement Rate',
    id: 'total.engagementRate',
    accessorKey: 'total.engagementRate',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.engagementRate');
    },
    cell: ({ row }) => {
      const percentValue = get(row.original, 'total.engagementRate');
      return <Percentage value={percentValue} aria-label={`Engagement Rate: ${percentValue || 'Not available'}`} />;
    },
  },
  {
    header: 'Likes',
    id: 'total.likes',
    accessorKey: 'total.likes',
    enableSorting: true,
    hidden: true,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.likes');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.likes');
      return <Generic align={'right'} value={value} aria-label={`Likes: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Comments',
    id: 'total.comments',
    accessorKey: 'total.comments',
    enableSorting: true,
    hidden: true,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.comments');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.comments');
      return <Generic align={'right'} value={value} aria-label={`Comments: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Shares',
    id: 'total.shares',
    accessorKey: 'total.shares',
    enableSorting: true,
    hidden: true,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.shares');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.shares');
      return <Generic align={'right'} value={value} aria-label={`Shares: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Saves',
    id: 'total.saves',
    accessorKey: 'total.saves',
    enableSorting: true,
    hidden: true,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.saves');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.saves');
      return <Generic align={'right'} value={value} aria-label={`Saves: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'Views',
    id: 'total.views',
    accessorKey: 'total.views',
    enableSorting: true,
    hidden: true,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.views');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.views');
      return <Generic align={'right'} value={value} aria-label={`Views: ${value ?? 'Not available'}`} />;
    },
  },
  {
    header: 'TMV',
    id: 'total.tmv',
    accessorKey: 'total.tmv',
    enableSorting: true,
    hidden: false,
    minSize: 200,
    csvValue: ({ data }) => {
      return get(data, 'total.tmv');
    },
    cell: ({ row }) => {
      const value = get(row.original, 'total.tmv');
      return <Currency value={value} aria-label={`TMV: ${value ?? 0}`} />;
    },
  },
  ];
